import React, { useEffect } from 'react';
import { Box, Alert } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/store';
import { fetchUser, updateUser } from 'store/actions/userActions';
import { apiResetPassword } from 'API/lotteryApi';
import type { ApiError } from 'API/apiClient';
import { UserFormFields, type UserFormValues } from 'components/Users/UserFormFields';
import RedButton from 'Styled/RedButton';
import TextButton from 'Styled/TextButton';
import { isFulfilled } from '@reduxjs/toolkit';
import { Permission, PermissionLabel, assignablePermissions } from 'types/enums/Permission';
import { usePermissions } from 'hooks/usePermissions';
import { PASSWORD_HINT, passwordSchema } from 'utility/passwordRules';
import { isSuperAdminUser } from 'utility/userPermissions';
import { PageHeader } from 'components/Layout/PageHeader';
import { preventEnterSubmit } from 'utility/preventEnterSubmit';

const validationSchema = Yup.object({
  username: Yup.string().min(5, 'Mindestens 5 Zeichen').required('Benutzername ist erforderlich'),
  email: Yup.string().email('Ungültige E-Mail').required('E-Mail ist erforderlich'),
  // An empty password field leaves the current password alone, as in the old app.
  password: passwordSchema,
  passwordRepeat: Yup.string().oneOf([Yup.ref('password')], 'Passwörter stimmen nicht überein'),
});

export const UserEditPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { lotteryId, userId } = useParams<{ lotteryId: string; userId: string }>();
  const { selectedUser } = useAppSelector((state) => state.users);
  const { isSuperAdmin } = usePermissions();
  const [pageError, setPageError] = React.useState<string | null>(null);

  useEffect(() => {
    if (userId) {
      dispatch(fetchUser(Number(userId)));
    }
  }, [dispatch, userId]);

  const backToList = () => navigate(`/lottery/${lotteryId}/users`);

  if (!selectedUser) return null;

  // Same rule as the backend: only a Super Admin may change a Super Admin.
  if (isSuperAdminUser(selectedUser) && !isSuperAdmin()) {
    return (
      <Box>
        <PageHeader title="Benutzer bearbeiten" />
        <Alert severity="warning" sx={{ marginBottom: 2 }}>
          Sie haben keine Berechtigung, dieses Benutzerkonto zu bearbeiten.
        </Alert>
        <TextButton onClick={backToList}>Zurück</TextButton>
      </Box>
    );
  }

  const currentPermissionId = Number(selectedUser.userAccountPermissions?.[0]?.permissionsId) || undefined;
  // Super Admin is not on offer, so an account that holds it would show an empty
  // select; keep the role the account already has in the list.
  const permissionOptions = assignablePermissions();
  if (currentPermissionId && !permissionOptions.some((option) => Number(option.id) === currentPermissionId)) {
    permissionOptions.push({ id: String(currentPermissionId), title: PermissionLabel[currentPermissionId as Permission] ?? 'Unbekannt' });
  }

  const initialValues: UserFormValues = {
    username: selectedUser.username || '',
    email: selectedUser.email || '',
    password: '',
    passwordRepeat: '',
    permissionId: currentPermissionId ? String(currentPermissionId) : '',
  };

  return (
    <Box>
      <PageHeader title="Benutzer bearbeiten" />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          setPageError(null);
          const result = await dispatch(
            updateUser({
              id: Number(userId),
              data: {
                username: values.username,
                email: values.email,
                permissionId: values.permissionId ? Number(values.permissionId) : undefined,
              },
            })
          );
          if (!isFulfilled(result)) return;

          if (values.password) {
            try {
              await apiResetPassword(Number(userId), values.password);
            } catch (error) {
              setPageError((error as ApiError).message);
              return;
            }
          }

          backToList();
        }}
      >
        {(form) => (
          <Form onKeyDown={preventEnterSubmit}>
            <Box sx={{ maxWidth: 600, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <UserFormFields form={form} options={permissionOptions} passwordHint={`Leer lassen, um das Passwort nicht zu ändern. ${PASSWORD_HINT}`} />

              {pageError && <Alert severity="error">{pageError}</Alert>}

              <Box sx={{ display: 'flex', gap: 2, marginTop: 2 }}>
                <RedButton type="submit" disabled={form.isSubmitting}>
                  Speichern
                </RedButton>
                <TextButton onClick={backToList}>Abbrechen</TextButton>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
